import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const LANG_KEY = "isi_lang";

export const resolveImage = (url) => {
  if (!url) return "";
  if (url.startsWith("/api/")) return `${BACKEND_URL}${url}`;
  return url;
};

function detectDefaultLang() {
  if (typeof window === "undefined") return "zh";
  const stored = localStorage.getItem(LANG_KEY);
  if (stored === "zh" || stored === "en") return stored;
  const nav = (navigator.language || navigator.userLanguage || "en").toLowerCase();
  // Treat any Chinese variant as zh
  if (nav.startsWith("zh")) return "zh";
  return "en";
}

const SiteContentContext = createContext(null);

export function SiteContentProvider({ children }) {
  const [bundle, setBundle] = useState(null); // {zh:{}, en:{}}
  const [lang, setLangState] = useState(() => detectDefaultLang());
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/site-content`);
      // Backward compat: if res.data has a 'hero' key directly, it's a single-lang response
      if (res.data?.zh || res.data?.en) {
        setBundle({ zh: res.data.zh || {}, en: res.data.en || res.data.zh || {} });
      } else {
        setBundle({ zh: res.data, en: res.data });
      }
    } catch (e) {
      console.error("Failed to load site content", e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const setLang = useCallback((next) => {
    setLangState(next);
    try { localStorage.setItem(LANG_KEY, next); } catch {}
    // Update <html lang=...>
    if (typeof document !== "undefined") {
      document.documentElement.lang = next === "zh" ? "zh-CN" : "en";
    }
  }, []);

  useEffect(() => {
    if (typeof document !== "undefined") {
      document.documentElement.lang = lang === "zh" ? "zh-CN" : "en";
    }
  }, [lang]);

  const content = bundle ? bundle[lang] || bundle.zh : null;

  return (
    <SiteContentContext.Provider value={{ content, bundle, lang, setLang, loading, refresh }}>
      {children}
    </SiteContentContext.Provider>
  );
}

export function useSiteContent() {
  const ctx = useContext(SiteContentContext);
  return ctx?.content;
}

export function useSiteContentState() {
  return useContext(SiteContentContext);
}

export function useLang() {
  const ctx = useContext(SiteContentContext);
  return { lang: ctx?.lang || "zh", setLang: ctx?.setLang || (() => {}) };
}
